import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, waLink } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Quote() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [makes, setMakes] = useState({});
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [pkgId, setPkgId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/car-makes").then((r) => setMakes(r.data));
  }, []);

  const svc = services.find((s) => s.id === serviceId);
  const pkg = svc?.packages.find((p) => p.id === pkgId);

  const submit = async (e) => {
    e.preventDefault();
    if (!make || !model || !serviceId || !pkgId) {
      toast.error("Complete the form to generate quote");
      return;
    }
    try {
      const res = await api.post("/quote", {
        car_make: make, car_model: model,
        service_id: serviceId, package_id: pkgId,
        name, phone,
      });
      setResult(res.data);
      toast.success("Quote generated");
    } catch {
      toast.error("Could not create quote");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="quote-page">
      <div className="overline text-[#FFB800]">Instant Quote</div>
      <h1 className="font-display text-4xl mt-2">Price your protection in 60 seconds.</h1>
      <p className="text-zinc-400 text-sm mt-3">Pick your car and service — we&apos;ll generate a quote reference you can share on WhatsApp.</p>

      <form onSubmit={submit} className="card-surface rounded-lg p-8 mt-10 grid sm:grid-cols-2 gap-5" data-testid="quote-form">
        <div>
          <label className="overline block mb-2">Car Make</label>
          <Select value={make} onValueChange={(v) => { setMake(v); setModel(""); }}>
            <SelectTrigger data-testid="quote-make" className="bg-[#0A0A0C] border-zinc-800 h-11"><SelectValue placeholder="Select make" /></SelectTrigger>
            <SelectContent className="bg-[#141417] border-zinc-800 text-zinc-100 max-h-64">
              {Object.keys(makes).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline block mb-2">Car Model</label>
          <Select value={model} onValueChange={setModel} disabled={!make}>
            <SelectTrigger data-testid="quote-model" className="bg-[#0A0A0C] border-zinc-800 h-11"><SelectValue placeholder={make ? "Select model" : "Pick make first"} /></SelectTrigger>
            <SelectContent className="bg-[#141417] border-zinc-800 text-zinc-100 max-h-64">
              {(makes[make] || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline block mb-2">Service</label>
          <Select value={serviceId} onValueChange={(v) => { setServiceId(v); setPkgId(""); }}>
            <SelectTrigger data-testid="quote-service" className="bg-[#0A0A0C] border-zinc-800 h-11"><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent className="bg-[#141417] border-zinc-800 text-zinc-100 max-h-64">
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline block mb-2">Package</label>
          <Select value={pkgId} onValueChange={setPkgId} disabled={!svc}>
            <SelectTrigger data-testid="quote-package" className="bg-[#0A0A0C] border-zinc-800 h-11"><SelectValue placeholder={svc ? "Select package" : "Pick service first"} /></SelectTrigger>
            <SelectContent className="bg-[#141417] border-zinc-800 text-zinc-100 max-h-64">
              {(svc?.packages || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} · ₹{p.price.toLocaleString()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline block mb-2">Your Name (optional)</label>
          <Input data-testid="quote-name" className="bg-[#0A0A0C] border-zinc-800 h-11" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="overline block mb-2">Phone (optional)</label>
          <Input data-testid="quote-phone" className="bg-[#0A0A0C] border-zinc-800 h-11" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91..." />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" data-testid="quote-submit" className="btn-primary">Generate Quote</button>
        </div>
      </form>

      {result && (
        <div className="card-surface rounded-lg p-8 mt-8" data-testid="quote-result">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Quote Ref</div>
              <div className="font-mono text-lg mt-1">{result.id}</div>
            </div>
            <div className="text-right">
              <div className="overline">Total</div>
              <div className="font-display text-3xl text-[#FFB800]">₹{result.price.toLocaleString()}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
            <div><div className="overline">Car</div><div className="mt-1">{result.car_make} {result.car_model}</div></div>
            <div><div className="overline">Service</div><div className="mt-1">{result.service_name}</div></div>
            <div><div className="overline">Package</div><div className="mt-1">{result.package_name}</div></div>
            <div><div className="overline">Turnaround</div><div className="mt-1">{result.duration_days} day(s)</div></div>
            <div><div className="overline">Warranty</div><div className="mt-1">{result.warranty_years > 0 ? `${result.warranty_years} year(s)` : "—"}</div></div>
          </div>
          <div className="flex gap-3 mt-8">
            <Link to={`/booking?service=${result.service_id}&pkg=${result.package_id}&make=${encodeURIComponent(result.car_make)}&model=${encodeURIComponent(result.car_model)}`}
              className="btn-primary" data-testid="quote-book">Book This</Link>
            <a target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="quote-share-wa"
              href={waLink(`Hi Tech Detailers, I'd like to book ${result.service_name} (${result.package_name}) for my ${result.car_make} ${result.car_model}. Quote: ${result.id}, ₹${result.price.toLocaleString()}.`)}>
              Share on WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
